import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { HiCheckCircle } from "react-icons/hi";
import { MdOutlineErrorOutline, MdOutlineHourglassEmpty } from "react-icons/md";
import LanguageSelector from "../components/atoms/LanguageSelector";
import { CustomButton } from "../components/atoms/CustomButton";
import { Spinner } from "../components/atoms/Spinner";
import {
  RegistrationPaymentCheck,
  RegistrationPaymentReturn,
  useCheckRegistrationPayment,
} from "../hooks/registrationPayment";
import isSafeRedirectUrl from "../utils/safeRedirect";
import { searchWithSecrets } from "../utils/addressSecrets";

const K = "registrationComplete.payment";
const LEAVE_AFTER_MS = 2000;

/** PayMod's return to a paid registration, signed for the client and the form; null on any other arrival. */
const paymentReturnOf = (search: string): RegistrationPaymentReturn | null => {
  const params = new URLSearchParams(search);
  const entityId = params.get("entity_identification");
  const userId = params.get("user_identification");
  const formId = params.get("form_id");
  const sig = params.get("sig");
  return entityId && userId && formId && sig
    ? { entityId, userId, formId, sig }
    : null;
};

type Check =
  | { kind: "checking" }
  | { kind: "invalid" }
  | { kind: "nothing" }
  | { kind: "failed" }
  | { kind: "answered"; answer: RegistrationPaymentCheck };

const Done: React.FC<{ title: React.ReactNode; text?: React.ReactNode }> = ({
  title,
  text,
}) => (
  <>
    <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-green-100">
      <HiCheckCircle className="h-12 w-12 text-green-600" aria-hidden="true" />
    </div>
    <p className="text-xl text-gray-700">{title}</p>
    {text && <p className="mt-2 text-sm text-gray-600">{text}</p>}
  </>
);

const Notice: React.FC<{
  tone: "amber" | "red";
  title: React.ReactNode;
  text: React.ReactNode;
}> = ({ tone, title, text }) => (
  <>
    <div
      className={
        tone === "amber"
          ? "mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-amber-100 text-amber-600"
          : "mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-red-100 text-red-600"
      }
    >
      {tone === "amber" ? (
        <MdOutlineHourglassEmpty size={28} aria-hidden="true" />
      ) : (
        <MdOutlineErrorOutline size={28} aria-hidden="true" />
      )}
    </div>
    <h1 className="text-xl font-semibold text-gray-900">{title}</h1>
    <p className="mt-2 text-sm text-gray-600">{text}</p>
  </>
);

const PaymentCheck: React.FC<{ paymentReturn: RegistrationPaymentReturn }> = ({
  paymentReturn,
}) => {
  const { t } = useTranslation("clientView");
  const { mutate, isLoading } = useCheckRegistrationPayment();
  const [check, setCheck] = useState<Check>({ kind: "checking" });

  const run = useCallback(() => {
    setCheck({ kind: "checking" });
    mutate(paymentReturn, {
      onSuccess: (answer) => setCheck({ kind: "answered", answer }),
      onError: (error) => {
        const status = error?.response?.status;
        setCheck({
          kind:
            status === 403 ? "invalid" : status === 404 ? "nothing" : "failed",
        });
      },
    });
  }, [mutate, paymentReturn]);

  const checked = useRef(false);
  useEffect(() => {
    if (checked.current) return;
    checked.current = true;
    run();
  }, [run]);

  const next =
    check.kind === "answered" && isSafeRedirectUrl(check.answer.redirect_url)
      ? (check.answer.redirect_url as string)
      : null;
  const paid = check.kind === "answered" && check.answer.status === "paid";

  useEffect(() => {
    if (!paid || !next) return;
    const id = window.setTimeout(
      () => window.location.assign(next),
      LEAVE_AFTER_MS
    );
    return () => window.clearTimeout(id);
  }, [paid, next]);

  if (check.kind === "checking") {
    return (
      <div
        className="flex items-center justify-center gap-2 py-6 text-sm text-gray-600"
        translate="no"
      >
        <Spinner size="sm" />
        {t(`${K}.checking`)}
      </div>
    );
  }

  if (check.kind === "invalid") {
    return (
      <Notice
        tone="red"
        title={t(`${K}.invalidTitle`)}
        text={t(`${K}.invalidText`)}
      />
    );
  }

  if (check.kind === "nothing") {
    return (
      <Done title={t("registrationComplete.message")} text={t(`${K}.none`)} />
    );
  }

  if (check.kind === "failed") {
    return (
      <div className="space-y-6">
        <Notice
          tone="red"
          title={t(`${K}.failedTitle`)}
          text={t(`${K}.failedText`)}
        />
        <CustomButton
          variant="secondary"
          title={t(`${K}.retry`) as string}
          onClick={run}
          isLoading={isLoading}
          className="w-full"
        />
      </div>
    );
  }

  if (paid) {
    return next ? (
      <div className="space-y-6">
        <Done title={t(`${K}.paidTitle`)} text={t(`${K}.leaving`)} />
        <CustomButton
          href={next}
          variant="primary"
          title={t(`${K}.continue`) as string}
          className="w-full"
        />
      </div>
    ) : (
      <Done
        title={t("registrationComplete.message")}
        text={t(`${K}.paidText`)}
      />
    );
  }

  const link = isSafeRedirectUrl(check.answer.payment_link)
    ? check.answer.payment_link
    : null;
  return (
    <div className="space-y-6">
      <Notice
        tone="amber"
        title={t(`${K}.pendingTitle`)}
        text={t(`${K}.pendingText`)}
      />
      <div className="space-y-3">
        {link && (
          <CustomButton
            href={link}
            variant="primary"
            title={t(`${K}.pay`) as string}
            className="w-full"
          />
        )}
        <CustomButton
          variant="secondary"
          title={t(`${K}.checkAgain`) as string}
          onClick={run}
          isLoading={isLoading}
          className="w-full"
        />
        {next && (
          <a
            href={next}
            className="block text-center text-sm font-medium text-blue-600 hover:text-blue-800"
          >
            {t(`${K}.continueUnpaid`)}
          </a>
        )}
      </div>
    </div>
  );
};

const RegistrationComplete = () => {
  const { t } = useTranslation("clientView");
  const location = useLocation();
  const [paymentReturn] = useState(() =>
    paymentReturnOf(
      searchWithSecrets(location.pathname, location.search).toString()
    )
  );

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-blue-100 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-xl shadow-lg relative">
        <div className="absolute top-4 right-4 border border-gray-200 rounded-full">
          <LanguageSelector />
        </div>

        <div className="text-center">
          {paymentReturn ? (
            <PaymentCheck paymentReturn={paymentReturn} />
          ) : (
            <Done title={t("registrationComplete.message")} />
          )}
        </div>
      </div>
    </div>
  );
};

export default RegistrationComplete;
